import type { GalleryEditRequest } from './editorjs-gallery-tool';

import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState, type ClipboardEvent } from 'react';

import { CenteredDialogContent } from '#/components/ui/centered-dialog-content';
import { toaster } from '#/components/ui/toaster';
import {
  DEFAULT_GALLERY_COLUMN_COUNT,
  MAX_GALLERY_COLUMN_COUNT,
  normalizeGalleryBlockData,
} from '#/pages/document/editorjs-types';
import type { GalleryBlockItem } from '#/pages/document/editorjs-types';
import { isImageFile } from '#/pages/document/upload-image';

export interface GalleryEditModalProps {
  request: GalleryEditRequest | null;
  onDismiss: () => void;
  /** When set, pasting an image into an image URL field uploads it and fills the URL. */
  onUpload?: (file: File) => Promise<string>;
}

function imageFileFromClipboardEvent(e: ClipboardEvent): File | null {
  const dt = e.clipboardData;
  if (!dt) return null;
  const { files } = dt;
  if (files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      const f = files.item(i);
      if (f && isImageFile(f)) return f;
    }
  }
  for (let i = 0; i < dt.items.length; i++) {
    const item = dt.items[i];
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file && isImageFile(file)) return file;
  }
  return null;
}

type GalleryEditRow = {
  id: string;
  url: string;
  caption: string;
};

function newRowId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `row-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function itemsToRows(items: GalleryBlockItem[]): GalleryEditRow[] {
  return items.map((item) => ({
    id: newRowId(),
    url: item.url,
    caption: item.caption ?? '',
  }));
}

export function GalleryEditModal({
  request,
  onDismiss,
  onUpload,
}: GalleryEditModalProps) {
  const open = request != null;
  const [columnCount, setColumnCount] = useState(DEFAULT_GALLERY_COLUMN_COUNT);
  const [rows, setRows] = useState<GalleryEditRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingRowIds, setUploadingRowIds] = useState(
    () => new Set<string>(),
  );

  useEffect(() => {
    if (request) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(request.initialJson);
      } catch (e) {
        toaster.create({
          title: 'Invalid gallery data',
          description:
            e instanceof Error ? e.message : 'Could not parse saved gallery',
          type: 'error',
        });
        setColumnCount(DEFAULT_GALLERY_COLUMN_COUNT);
        setRows([]);
        return;
      }
      const data = normalizeGalleryBlockData(parsed);
      setColumnCount(data.columnCount ?? DEFAULT_GALLERY_COLUMN_COUNT);
      setRows(itemsToRows(data.items));
    } else {
      setSaving(false);
    }
  }, [request]);

  const handleClose = () => {
    if (saving) return;
    onDismiss();
  };

  const handleSave = async () => {
    if (!request) return;

    const items: GalleryBlockItem[] = [];
    for (const row of rows) {
      const url = row.url.trim();
      if (!url) continue;
      const cap = row.caption.trim();
      const item: GalleryBlockItem = { url };
      if (cap) item.caption = cap;
      items.push(item);
    }

    const data = normalizeGalleryBlockData({ columnCount, items });
    setSaving(true);
    try {
      await request.onCommit(data);
      onDismiss();
    } catch (error) {
      toaster.create({
        title: 'Could not save gallery',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: newRowId(), url: '', caption: '' }]);
  };

  const updateRow = (
    id: string,
    patch: Partial<Pick<GalleryEditRow, 'url' | 'caption'>>,
  ) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const moveRow = (index: number, delta: number) => {
    const next = index + delta;
    if (next < 0 || next >= rows.length) return;
    setRows((prev) => {
      const copy = [...prev];
      const [removed] = copy.splice(index, 1);
      copy.splice(next, 0, removed);
      return copy;
    });
  };

  const setColumnsFromInput = (raw: string) => {
    if (raw === '') {
      setColumnCount(DEFAULT_GALLERY_COLUMN_COUNT);
      return;
    }
    const v = parseInt(raw, 10);
    if (!Number.isFinite(v)) return;
    const clamped = Math.min(
      MAX_GALLERY_COLUMN_COUNT,
      Math.max(1, Math.floor(v)),
    );
    setColumnCount(clamped);
  };

  const handleUrlPaste = async (rowId: string, e: ClipboardEvent) => {
    if (!onUpload || saving) return;
    const file = imageFileFromClipboardEvent(e);
    if (!file) return;
    e.preventDefault();
    setUploadingRowIds((prev) => new Set(prev).add(rowId));
    try {
      const url = await onUpload(file);
      updateRow(rowId, { url });
    } catch (error) {
      toaster.create({
        title: 'Image upload failed',
        description:
          error instanceof Error ? error.message : 'Could not upload image',
        type: 'error',
      });
    } finally {
      setUploadingRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="xl"
    >
      <CenteredDialogContent maxW="min(90vw, 56rem)">
        <DialogHeader>
          <DialogTitle>Edit gallery</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Set how many images appear per row, then add image URLs (and
              optional captions). Use the buttons on each row to reorder or
              remove items.
              {onUpload
                ? ' Paste an image into an image URL field to upload it and fill the URL.'
                : ''}
            </Text>
            <HStack gap={3} align="center" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                Columns per row
              </Text>
              <Input
                type="number"
                min={1}
                max={MAX_GALLERY_COLUMN_COUNT}
                w="5.5rem"
                value={columnCount}
                onChange={(e) => setColumnsFromInput(e.target.value)}
                disabled={saving}
              />
              <Text fontSize="xs" color="fg.muted">
                1–{MAX_GALLERY_COLUMN_COUNT}
              </Text>
            </HStack>
            <Button
              alignSelf="flex-start"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={saving}
            >
              Add image
            </Button>
            <Box maxH="360px" overflowY="auto" pr={1}>
              <VStack gap={3} align="stretch">
                {rows.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    No images yet. Click &quot;Add image&quot; to add a row.
                  </Text>
                ) : (
                  rows.map((row, index) => (
                    <Box key={row.id} borderWidth="1px" borderRadius="md" p={3}>
                      <VStack gap={2} align="stretch">
                        <Text fontSize="xs" color="fg.muted">
                          Image {index + 1}
                        </Text>
                        <Textarea
                          placeholder="Image URL (paste an image to upload)"
                          value={row.url}
                          onChange={(e) =>
                            updateRow(row.id, { url: e.target.value })
                          }
                          onPaste={(e) => {
                            void handleUrlPaste(row.id, e);
                          }}
                          rows={2}
                          resize="vertical"
                          minH="2.5rem"
                          disabled={saving || uploadingRowIds.has(row.id)}
                        />
                        <Input
                          placeholder="Caption (optional)"
                          value={row.caption}
                          onChange={(e) =>
                            updateRow(row.id, { caption: e.target.value })
                          }
                          disabled={saving}
                        />
                        <HStack gap={2} flexWrap="wrap">
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => moveRow(index, -1)}
                            disabled={saving || index === 0}
                            aria-label="Move image up"
                          >
                            Move up
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => moveRow(index, 1)}
                            disabled={saving || index === rows.length - 1}
                            aria-label="Move image down"
                          >
                            Move down
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => removeRow(row.id)}
                            disabled={saving}
                            aria-label="Remove image"
                          >
                            Remove
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>
                  ))
                )}
              </VStack>
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button colorScheme="blue" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
